import { useEffect, useRef } from 'react';
import { type DefaultValues, type FieldValues, useForm, useFormState } from 'react-hook-form';

/**
 * Re-seeds the form whenever `seedKey` changes, and whenever `data` changes while there are no
 * unsaved edits, so a refetch never overwrites what the user is typing.
 */
export const useSeededForm = <TValues extends FieldValues>(data: TValues, seedKey = '') => {
  const formMethods = useForm<TValues>({
    defaultValues: data as DefaultValues<TValues>,
    mode: 'onChange',
  });
  const { isDirty } = useFormState({ control: formMethods.control });
  const seeded = useRef({ seedKey, data });

  useEffect(() => {
    const isNewSeed = seeded.current.seedKey !== seedKey;

    if (!isNewSeed && (seeded.current.data === data || isDirty)) return;

    seeded.current = { seedKey, data };
    formMethods.reset(data);
  }, [data, seedKey, formMethods, isDirty]);

  return { formMethods, isDirty };
};
